import * as React from 'react';

export type State = {
  /** Map Related */
  ceLat?: number;
  ceLng?: number;
  diagonal?: number;
  neLat?: number;
  neLng?: number;
  swLat?: number;
  swLng?: number;
  zoom?: number;
  searchByMap?: boolean;
  /** Form Related */
  minPrice?: number;
  maxPrice?: number;
  minSurface?: number;
  maxSurface?: number;
  minRooms?: number;
  maxRooms?: number;
  minBathrooms?: number;
  maxBathrooms?: number;
  isNew?: boolean;
  isExcellent?: boolean;
  isGood?: boolean;
  isToBeRefurbished?: boolean;
  /** Pagination */
  currentPage?: number;
  /** Search Box **/
  cityId?: number;
  provinceId?: number;
  regionId?: number;
};

type ApplyFilter = {
  type: 'APPLY_FILTER';
  payload: State;
};

type ChangePage = {
  type: 'CHANGE_PAGE';
  payload: number;
};

export type Action = ApplyFilter | ChangePage;

export function useQueryFilters(filters: any) {
  return React.useReducer(
    (state: Partial<State>, action: Action) => {
      switch (action.type) {
        case 'APPLY_FILTER':
          return { ...state, ...action.payload, currentPage: 1 };
        case 'CHANGE_PAGE':
          return { ...state, currentPage: action.payload };
        default:
          throw new Error('Unknown action');
      }
    },
    {
      currentPage: 1,
      ...filters,
    },
  );
}

export function createStateFromQs(qs: any): State {
  const ss = {
    ...qs,
    ...(qs.ceLat && { ceLat: Number(qs.ceLat) }),
    ...(qs.ceLng && { ceLng: Number(qs.ceLng) }),
    ...(qs.diagonal && { diagonal: Number(qs.diagonal) }),
    ...(qs.neLat && { neLat: Number(qs.neLat) }),
    ...(qs.neLng && { neLng: Number(qs.neLng) }),
    ...(qs.swLat && { swLat: Number(qs.swLat) }),
    ...(qs.swLng && { swLng: Number(qs.swLng) }),
    ...(qs.zoom && { zoom: Number(qs.zoom) }),
    ...(qs.currentPage && { currentPage: Number(qs.currentPage) }),
  };

  return ss;
}

/** QueryFilters */

export function createPaginationOffset(currentPage = 1, chunksPerPage = 20) {
  if (currentPage < 1) return 0;
  return (currentPage - 1) * chunksPerPage;
}

export function createQueryFilters(data: State, opts = { itemsPerPage: 20 }) {
  const isStatusFiltered = data.isNew || data.isExcellent || data.isGood || data.isToBeRefurbished;

  const isGeoFiltered = data.ceLat && data.ceLng && data.diagonal;
  const isLocationFiltered = data.cityId || data.provinceId || data.regionId;

  return {
    limit: opts.itemsPerPage,
    offset: createPaginationOffset(data.currentPage, opts.itemsPerPage),
    order: ['auctionStatus ASC'],
    where: {
      ...(isGeoFiltered && {
        geo: {
          near: {
            lat: data.ceLat,
            lon: data.ceLng,
            maxDistance: data.diagonal,
            unit: 'kilometers',
          },
        },
      }),
    },
    include: [
      {
        relation: 'location',
        required: true,
        scope: {
          ...(isLocationFiltered && {
            where: {
              ...(data.cityId && { cityId: data.cityId }),
              ...(data.provinceId && { provinceId: data.provinceId }),
              ...(data.regionId && { regionId: data.regionId }),
            },
          }),
        },
      },
      {
        relation: 'multimedia',
        scope: {
          include: [
            {
              relation: 'cover',
            },
            {
              relation: 'images',
              scope: {
                order: 'order ASC',
              },
            },
          ],
        },
      },
      { relation: 'descriptions' },
      {
        relation: 'auction',
        required: true,
        scope: {
          where: {
            ...((data.minPrice || data.maxPrice) && {
              and: [
                data.minPrice && {
                  'buyNowPrice.amount': { gte: Number(data.minPrice) },
                },
                data.maxPrice && {
                  'buyNowPrice.amount': { lte: Number(data.maxPrice) },
                },
              ],
            }),
          },
        },
      },
      { relation: 'foreclosureSale' },
      { relation: 'economics', required: true },
      {
        relation: 'features',
        required: true,
        scope: {
          where: {
            ...((data.minSurface || data.maxSurface) && {
              surface: {
                ...(data.minSurface && { gte: data.minSurface }),
                ...(data.maxSurface && { lte: data.maxSurface }),
              },
            }),
            ...((data.minRooms || data.maxRooms) && {
              rooms: {
                ...(data.minRooms && { gte: data.minRooms }),
                ...(data.maxRooms && { lte: data.maxRooms }),
              },
            }),
            ...((data.minBathrooms || data.maxBathrooms) && {
              bathrooms: {
                ...(data.minBathrooms && { gte: data.minBathrooms }),
                ...(data.maxBathrooms && { lte: data.maxBathrooms }),
              },
            }),
            ...(isStatusFiltered && {
              or: [
                data.isNew && { condition: 'new_under_construction' },
                data.isExcellent && { condition: 'great_renovated' },
                data.isGood && { condition: 'good_habitable' },
                data.isToBeRefurbished && { condition: 'to_be_restructured' },
              ],
            }),
          },
        },
      },
    ],
  };
}

export function createQueryMapFilters(data: State) {
  const isStatusFiltered = data.isNew || data.isExcellent || data.isGood || data.isToBeRefurbished;
  const isLocationFiltered = data.cityId || data.provinceId || data.regionId;

  return {
    include: [
      {
        relation: 'location',
        required: true,
        scope: {
          ...(isLocationFiltered && {
            where: {
              ...(data.cityId && { cityId: data.cityId }),
              ...(data.provinceId && { provinceId: data.provinceId }),
              ...(data.regionId && { regionId: data.regionId }),
            },
          }),
        },
      },
      {
        relation: 'multimedia',
        scope: {
          include: [
            {
              relation: 'cover',
            },
            {
              relation: 'images',
              scope: {
                order: 'order ASC',
              },
            },
          ],
        },
      },
      {
        relation: 'descriptions',
        required: false,
        scope: {
          fields: ['title'],
        },
      },
      {
        relation: 'auction',
        required: true,
        scope: {
          where: {
            ...((data.minPrice || data.maxPrice) && {
              and: [
                data.minPrice && {
                  'buyNowPrice.amount': { gte: Number(data.minPrice) },
                },
                data.maxPrice && {
                  'buyNowPrice.amount': { lte: Number(data.maxPrice) },
                },
              ],
            }),
          },
        },
      },
      { relation: 'foreclosureSale' },
      {
        relation: 'economics',
        required: true,
      },
      {
        relation: 'features',
        required: true,
        scope: {
          fields: [],
          where: {
            ...((data.minSurface || data.maxSurface) && {
              surface: {
                ...(data.minSurface && { gte: data.minSurface }),
                ...(data.maxSurface && { lte: data.maxSurface }),
              },
            }),
            ...((data.minRooms || data.maxRooms) && {
              rooms: {
                ...(data.minRooms && { gte: data.minRooms }),
                ...(data.maxRooms && { lte: data.maxRooms }),
              },
            }),
            ...((data.minBathrooms || data.maxBathrooms) && {
              bathrooms: {
                ...(data.minBathrooms && { gte: data.minBathrooms }),
                ...(data.maxBathrooms && { lte: data.maxBathrooms }),
              },
            }),
            ...(isStatusFiltered && {
              or: [
                data.isNew && { condition: 'new_under_construction' },
                data.isExcellent && { condition: 'great_renovated' },
                data.isGood && { condition: 'good_habitable' },
                data.isToBeRefurbished && { condition: 'to_be_restructured' },
              ],
            }),
          },
        },
      },
    ],
  };
}
